import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AccountService, AuthService, BillingV2Service, FeaturesService, FilesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { ApplicationDialogsService } from '@shared/application-dialogs';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getMe } from '@store/reducers/profile.reducer';
import { EUserServiceType, IAccountStats, ImageSizeEnum, SubjectCategoryEnum, User, UserService } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-user-service-surface-block',
  templateUrl: './user-service-surface-block.component.html',
  styleUrls: ['./user-service-surface-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceSurfaceBlockComponent extends ChangableComponent implements OnInit {
  @Input() public set userService(userService: UserService) {
    this._userService = userService;
    this.setupCard();
  }
  public get userService(): UserService {
    return this._userService;
  }
  public me: User;
  public isGuest: boolean;
  public accountRoute;
  public userServiceRoute;
  public authorFullName: string;
  public subject: string;
  public isNativeLanguage: boolean;
  public title: string;
  public excerpt: string;
  public description: string;
  public level: string;
  public coverImageUrl: string;
  public category: string;
  public discount: number;
  public duration: number;
  public price_tier: number;
  public stats: Partial<IAccountStats>;
  public checkoutModalOpen: boolean;
  public authorAvatar: OnUiCover;
  public coverImage: Partial<OnUiCover>;

  private _userService: UserService;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    private features: FeaturesService,
    private authService: AuthService,
    private accountService: AccountService,
    private billingV2Service: BillingV2Service,
    private store: Store,
    protected dialogsService: ApplicationDialogsService,
    protected cdr: ChangeDetectorRef,
  ) {
    super(cdr);
  }

  ngOnInit() {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.isGuest = !this.authService.isAuthenticated;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private getAuthorDetails(user: User) {
    if (!user) {
      return;
    }

    this.stats = user.stats;
    this.authorFullName = AccountService.getUserName(user);
    this.accountRoute = AccountService.accountRoute(user);
    this.authorAvatar = {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(user?.userpic_id, ImageSizeEnum.sm),
      title: this.authorFullName,
      link: this.accountRoute.join('/'),
      form: 'circle',
    };
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.getAuthorDetails(user);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected setupCard() {
    if (this.userService.author) {
      this.getAuthorDetails(this.userService.author);
    } else {
      this.loadAuthorDetails(this.userService.author_id);
    }
    this.coverImageUrl = FilesService.getFileUrlBySize(this.userService?.cover_id, ImageSizeEnum.wide);
    this.userServiceRoute = LibraryRouter.userServiceRouteUniversal(this.userService);
    this.subject = this.userService?.subject?.title;
    this.isNativeLanguage = this.userService?.subject?.category_id === SubjectCategoryEnum.native_language;
    // TODO this.level = this.userService.level ? this.userService.level.title : 'subject-levels.any-level';
    this.title = this.userService?.title;
    this.excerpt = this.userService?.excerpt;
    this.description = this.userService?.description;
    this.category = this.userService?.category?.title;
    this.discount = this.userService?.discount;
    this.duration = this.userService?.duration;
    this.price_tier = this.userService?.price_tier;

    this.coverImage = {
      img: this.coverImageUrl,
      title: this.userService.title,
    };
  }

  public getFormattedAmount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.price_tier);
  }

  public isRegularClass(): boolean {
    return this.userService.type === EUserServiceType.regular;
  }

  public openCheckoutModal() {
    const onAuthComplete = () => {
      this.checkoutModalOpen = true;
      this.dialogsService.openUserServiceCheckoutWizard({
        userServiceId: this.userService.id,
      });
      this.detectChanges();
    };

    if (this.isGuest) {
      this.authService.showAuthModal(onAuthComplete);
    } else {
      onAuthComplete();
    }
  }

  public closeCheckoutModal() {
    this.checkoutModalOpen = false;
    this.detectChanges();
  }
}
